import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { AuthService } from '@app/modules/auth/auth.service'
import { TranslateService } from '@ngx-translate/core'

import { Driver } from '@app/shared/models/driver.model'
import { ReassignError } from '@app/shared/models/route.model'
import dayjs from 'dayjs'

@Component({
  selector: 'lin-assign-route-dialog',
  templateUrl: './assign-route-dialog.component.html',
  styleUrls: ['./assign-route-dialog.component.scss'],
  standalone: false,
})
export class AssignRouteDialogComponent implements OnInit {
  routesToReassign = new Set<number>()
  overlappingRoutes: { [key: string]: string[] } = {}
  routesExpanded = false

  get singleReassignMessage(): string {
    const { driver, data } = this.data
    const overlappingRoutesText = Object.values(this.overlappingRoutes)[0].join(` ${<string>this.translate.instant('NOTIFICATIONS.AND')} `)

    return <string>this.translate.instant('DIALOGS.ROUTES_BY_VEHICLE.SINGLE_REASSIGN_MESSAGE', {
      driver: driver.fullName,
      overlappingRoutesText,
      regNo: data[0].route.regNo,
    })
  }

  private get dateFormat(): string {
    return this.authService.getCurrentUserDateFormat() + ' HH:mm'
  }

  constructor(
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
    private readonly dialogRef: MatDialogRef<AssignRouteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { data: ReassignError[]; message?: string; driver: Driver; bulk?: boolean },
  ) {}

  ngOnInit(): void {
    this.data.data.forEach((route) => {
      this.overlappingRoutes[route.route.id] = Object.values(route.unassignRoutes).map(
        (unassignRoute) => <string>this.translate.instant('DIALOGS.ROUTES_BY_VEHICLE.OVERLAPPING_ROUTE_TEXT', {
            regNo: unassignRoute.regNo,
            startDate: this.formatDate(unassignRoute.pointStart.lastCoordinates.ts),
            endDate: this.formatDate(unassignRoute.pointFinish.lastCoordinates.ts),
          }),
      )
    })
  }

  handleCheckRoute(value: boolean, routeId: number | string): void {
    value ? this.routesToReassign.add(+routeId) : this.routesToReassign.delete(+routeId)
  }

  getCheckboxText(route: ReassignError): string {
    const currentRouteDate = `${this.formatDate(route.route.pointStart.lastCoordinates.ts)} - ${this.formatDate(
      route.route.pointFinish.lastCoordinates.ts,
    )}`
    const overlappingRoutesText = this.overlappingRoutes[route.route.id].join(` ${<string>this.translate.instant('NOTIFICATIONS.AND')} `)

    return <string>this.translate.instant('DIALOGS.ROUTES_BY_VEHICLE.REASSIGN_ROUTE_CHECKBOX', {
      currentRouteDate,
      driver: this.data.driver.fullName,
      overlappingRoutesText,
    })
  }

  reassignBulk(mode: 'force' | 'detailed'): void {
    const routesToReassign = mode === 'force' ? this.data.data.map((route) => +route.route.id) : Array.from(this.routesToReassign)
    const vehiclesToCheck = {}

    this.data.data.forEach((routeError) => {
      if (routesToReassign.includes(+routeError.route.id)) {
        Object.values(routeError.unassignRoutes).forEach((unassignRoute) => {
          vehiclesToCheck[unassignRoute.vehicleId]
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
              vehiclesToCheck[unassignRoute.vehicleId].push(+unassignRoute.id)
            : (vehiclesToCheck[unassignRoute.vehicleId] = [+unassignRoute.id])
        })
      }
    })

    this.dialogRef.close({ vehiclesToCheck, routesToReassign })
  }

  private formatDate = (date: string): string => dayjs(date).tz().format(this.dateFormat)
}
